import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  ChevronDown,
  Loader2,
  LocateFixed,
  MapPin,
  Send,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal } from "./Reveal";
import {
  DEFAULT_COUNTRY_CODE,
  ENQUIRY_TYPES,
  TIME_SLOTS,
  validateSubmission,
  type SubmissionInput,
} from "@/lib/contact";

type Status = "idle" | "locating" | "submitting" | "sent" | "error";

type FormState = {
  name: string;
  phone: string;
  date: string;
  timeSlot: string;
  enquiryType: string;
  message: string;
  location: string;
};

const EMPTY: FormState = {
  name: "",
  phone: "",
  date: "",
  timeSlot: "",
  enquiryType: "",
  message: "",
  location: "",
};

/** Friendly copy for each GeolocationPositionError code. */
function geoErrorMessage(code: number) {
  switch (code) {
    case 1:
      return "Location permission was declined. You can still type your area below.";
    case 2:
      return "Couldn't get a position fix. Try again, or type your area below.";
    case 3:
      return "Locating timed out. Try again, or type your area below.";
    default:
      return "This browser can't share your location. You can type your area below.";
  }
}

/** Rough accuracy label so the fill does not read as authoritative. */
function accuracyLabel(metres: number) {
  if (metres < 100) return "precise";
  if (metres < 1000) return "approximate";
  return "rough";
}

/**
 * Reverse geocodes coordinates to a readable place using the public Nominatim
 * endpoint. Deliberately best-effort: if it fails we keep the coordinates and
 * let the visitor type their own area, rather than blocking the form.
 */
async function reverseGeocode(lat: number, lon: number) {
  const url = new URL("https://nominatim.openstreetmap.org/reverse");
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lon", String(lon));
  url.searchParams.set("zoom", "10");

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return null;
    const data = (await response.json()) as { display_name?: string };
    return data.display_name ?? null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

const labelClass =
  "block font-sans text-[0.625rem] tracking-[0.2em] text-muted-foreground uppercase";
const optionalClass = "ml-1.5 text-[0.55rem] tracking-[0.12em] opacity-60 normal-case";
/**
 * Bottom-rule inputs. The vertical padding is deliberate: a bare `pb` gives a
 * ~31px control, which is too small to hit reliably on a phone. `py-3` keeps
 * the thin editorial rule but brings every field up to a ~44px tap target.
 */
const fieldClass =
  "mt-2 w-full border-b border-border bg-transparent py-3 font-sans text-sm font-light " +
  "text-foreground transition-colors duration-300 outline-none placeholder:text-muted-foreground/50 " +
  "focus:border-gold";

/** `appearance-none` strips the native arrow, so supply our own. */
function SelectShell({ id, value, onChange, children }: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <select
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        className={cn(fieldClass, "cursor-pointer appearance-none pr-7")}
      >
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-0 bottom-3.5 h-3.5 w-3.5 text-muted-foreground/70"
        strokeWidth={1.5}
      />
    </div>
  );
}

export function ContactForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [geoNote, setGeoNote] = useState("");
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);

  /**
   * Swapping the tall form for the much shorter thank-you panel collapses the
   * document, and the browser clamps scrollTop when that happens — which reads
   * to the visitor as the page lurching on submit. Measuring the form first and
   * giving the panel the same min-height keeps the page height identical, so
   * there is nothing to clamp and nothing to lurch.
   */
  const formRef = useRef<HTMLFormElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const [formHeight, setFormHeight] = useState<number | null>(null);

  /**
   * Move focus to the confirmation so screen readers announce it, but take
   * control of scrolling ourselves: `preventScroll` stops the browser's own
   * jump, and we only scroll when the confirmation is not already comfortably
   * in view. Because the panel reserves the form's height, that scroll is a
   * short, smooth settle rather than the old lurch.
   */
  useEffect(() => {
    if (status !== "sent") return;
    const node = successRef.current;
    if (!node) return;

    node.focus({ preventScroll: true });

    const rect = node.getBoundingClientRect();
    const viewport = window.innerHeight;
    if (rect.top >= 0 && rect.bottom <= viewport) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const target = window.scrollY + rect.top + rect.height / 2 - viewport / 2;
    window.scrollTo({ top: Math.max(0, target), behavior: reduce ? "auto" : "smooth" });
  }, [status]);

  /** Reverse geocoding can land after the visitor typed their own wording, so
   *  only auto-fill a location field the visitor has not touched. */
  const locationTouched = useRef(false);
  useEffect(() => {
    if (form.location.length > 0) locationTouched.current = true;
  }, [form.location]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear the field-level error as soon as that field is edited.
    setErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const locate = () => {
    if (!("geolocation" in navigator)) {
      setGeoNote("This browser can't share your location — please type your area.");
      return;
    }
    setStatus("locating");
    setGeoNote("");

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude, accuracy } = position.coords;
        setCoords({ lat: latitude, lon: longitude });
        setGeoNote(`Location captured (${accuracyLabel(accuracy)}). Looking up your area…`);
        setStatus("idle");

        void reverseGeocode(latitude, longitude).then(place => {
          if (place && !locationTouched.current) {
            set("location", place);
            setGeoNote("Location filled in — edit it if that's not quite right.");
          } else if (place) {
            setGeoNote("Got your coordinates. Your typed location was kept.");
          } else {
            setGeoNote("Got your coordinates. Please type your area below.");
          }
        });
      },
      error => {
        setStatus("idle");
        setGeoNote(geoErrorMessage(error.code));
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    );
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Reuse the exact validator the server runs, so the visitor gets instant
    // feedback and the two can never drift apart.
    const result = validateSubmission({
      name: form.name,
      phone: form.phone,
      preferredTime: [form.date, form.timeSlot].filter(Boolean).join(" · "),
      enquiryType: form.enquiryType,
      message: form.message,
      location: form.location,
      latitude: coords?.lat ?? null,
      longitude: coords?.lon ?? null,
    });

    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setStatus("submitting");

    const payload: SubmissionInput = {
      ...result.value,
      name: result.value.name,
      phone: result.value.phone,
    };

    try {
      const response = await fetch("/api/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        fields?: Record<string, string>;
      };

      if (!response.ok || !data.ok) {
        setErrors(data.fields ?? {});
        setStatus("error");
        return;
      }

      // Measure while the form is still mounted: the thank-you panel reserves
      // this height so the document never shrinks and the page never jumps.
      setFormHeight(formRef.current?.offsetHeight ?? null);
      setStatus("sent");
      setForm(EMPTY);
      setCoords(null);
      setGeoNote("");
    } catch {
      setStatus("error");
      setErrors({
        form: "Couldn't reach the server. Please check your connection, or message me on WhatsApp.",
      });
    }
  };

  if (status === "sent") {
    return (
      <div
        ref={successRef}
        tabIndex={-1}
        style={formHeight ? { minHeight: formHeight } : undefined}
        className="relative flex flex-col items-center justify-center overflow-hidden border border-gold/40 bg-gold/5 px-6 py-20 text-center outline-none"
      >
        {/* A single gold highlight travels the top edge, then rests. */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px">
          <span className="animate-sweep-line block h-px w-1/3 bg-gradient-to-r from-transparent via-gold to-transparent" />
        </span>

        <span className="relative flex h-16 w-16 items-center justify-center">
          {/* Expanding halo, timed to land as the tick is drawn. */}
          <span
            aria-hidden="true"
            className="animate-pulse-ring absolute inset-0 rounded-full border border-gold/50"
            style={{ animationDelay: "0.2s" }}
          />
          <span className="animate-success-pop flex h-16 w-16 items-center justify-center rounded-full border border-gold bg-background">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="h-7 w-7 text-gold"
              style={{ "--draw-len": "24" } as React.CSSProperties}
            >
              <path
                d="M4 12.5 9.5 18 20 6.5"
                pathLength={24}
                strokeDasharray="24"
                className="animate-draw-stroke"
                style={{ animationDelay: "0.34s" }}
              />
            </svg>
          </span>
        </span>

        <h3
          className="mt-7 animate-rise-in font-display text-2xl text-foreground sm:text-3xl"
          style={{ animationDelay: "0.42s" }}
        >
          Thank you — it's with me.
        </h3>
        <p
          className="mt-3 max-w-sm animate-rise-in font-sans text-sm leading-relaxed font-light text-muted-foreground"
          style={{ animationDelay: "0.54s" }}
        >
          I'll reply to your number shortly to confirm timing. If it's urgent, WhatsApp is
          the fastest way to reach me.
        </p>
        <button
          type="button"
          onClick={() => {
            setFormHeight(null);
            setStatus("idle");
          }}
          className="mt-8 inline-flex animate-rise-in cursor-pointer items-center gap-2 font-sans text-[0.625rem] tracking-[0.22em] text-muted-foreground uppercase transition-colors hover:text-gold"
          style={{ animationDelay: "0.66s" }}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="relative">
      {/* Honeypot — hidden from people, irresistible to bots. */}
      <div aria-hidden="true" className="absolute h-0 w-0 overflow-hidden opacity-0">
        <label htmlFor="cf-company">Company</label>
        <input id="cf-company" name="company" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="grid gap-x-10 gap-y-8 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className={labelClass}>
            Your name <span className="text-gold">*</span>
          </label>
          <input
            id="cf-name"
            name="name"
            value={form.name}
            onChange={e => set("name", e.target.value)}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? "cf-name-error" : undefined}
            autoComplete="name"
            placeholder="Priya Sharma"
            className={cn(fieldClass, errors.name && "border-destructive")}
          />
          {errors.name && (
            <p id="cf-name-error" className="mt-2 flex items-center gap-1.5 font-sans text-[0.7rem] text-destructive">
              <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={1.5} />
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="cf-phone" className={labelClass}>
            Phone / WhatsApp <span className="text-gold">*</span>
          </label>
          <div
            className={cn(
              "mt-2 flex items-center gap-2 border-b border-border transition-colors duration-300 focus-within:border-gold",
              errors.phone && "border-destructive",
            )}
          >
            <span className="font-sans text-sm font-light text-muted-foreground select-none">
              {DEFAULT_COUNTRY_CODE}
            </span>
            <input
              id="cf-phone"
              name="phone"
              inputMode="tel"
              value={form.phone}
              onChange={e => set("phone", e.target.value)}
              onBlur={() =>
                set("phone", form.phone.replace(/\s+/g, " ").trim())
              }
              aria-invalid={Boolean(errors.phone)}
              aria-describedby={errors.phone ? "cf-phone-error" : "cf-phone-hint"}
              autoComplete="tel"
              placeholder="9820730567"
              className="w-full bg-transparent py-3 font-sans text-sm font-light text-foreground outline-none placeholder:text-muted-foreground/50"
            />
          </div>
          <p id="cf-phone-hint" className="sr-only">
            Nepali numbers, entered without the country code.
          </p>
          {errors.phone && (
            <p id="cf-phone-error" className="mt-2 flex items-center gap-1.5 font-sans text-[0.7rem] text-destructive">
              <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={1.5} />
              {errors.phone}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="cf-type" className={labelClass}>
            What are you booking <span className={optionalClass}>optional</span>
          </label>
          <SelectShell id="cf-type" value={form.enquiryType} onChange={v => set("enquiryType", v)}>
            <option value="">Not sure yet</option>
            {ENQUIRY_TYPES.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </SelectShell>
        </div>

        <div>
          <label htmlFor="cf-time" className={labelClass}>
            Preferred time <span className={optionalClass}>optional</span>
          </label>
          <SelectShell id="cf-time" value={form.timeSlot} onChange={v => set("timeSlot", v)}>
            <option value="">Any time works</option>
            {TIME_SLOTS.map(slot => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </SelectShell>
        </div>

        <div>
          <label htmlFor="cf-date" className={labelClass}>
            Preferred date <span className={optionalClass}>optional</span>
          </label>
          <input
            id="cf-date"
            name="date"
            type="date"
            value={form.date}
            min={new Date().toISOString().slice(0, 10)}
            onChange={e => set("date", e.target.value)}
            className={cn(fieldClass, "cursor-pointer")}
          />
        </div>

        <div className="sm:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label htmlFor="cf-location" className={labelClass}>
              Your area <span className={optionalClass}>optional</span>
            </label>
            {/* min-h keeps this a real tap target; it is a headline feature, not
                a bit of secondary text. */}
            <button
              type="button"
              onClick={locate}
              disabled={status === "locating"}
              className="-my-2 inline-flex min-h-[44px] cursor-pointer items-center gap-2 border border-border px-3 font-sans text-[0.625rem] tracking-[0.16em] text-muted-foreground uppercase transition-colors hover:border-gold hover:text-gold disabled:cursor-wait disabled:opacity-60"
            >
              {status === "locating" ? (
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
              ) : (
                <LocateFixed className="h-3 w-3" strokeWidth={1.5} />
              )}
              {status === "locating" ? "Locating…" : "Use my current location"}
            </button>
          </div>

          <div className="relative mt-2.5">
            <MapPin
              className="pointer-events-none absolute bottom-4 left-0 h-3.5 w-3.5 text-muted-foreground/60"
              strokeWidth={1.5}
            />
            <input
              id="cf-location"
              name="location"
              value={form.location}
              onChange={e => set("location", e.target.value)}
              autoComplete="address-level2"
              placeholder="Kathmandu, Nepal"
              className={cn(fieldClass, "pl-6")}
            />
            {form.location && (
              <button
                type="button"
                onClick={() => {
                  set("location", "");
                  setCoords(null);
                  setGeoNote("");
                }}
                aria-label="Clear location"
                className="absolute right-0 bottom-2 flex h-8 w-8 cursor-pointer items-center justify-center text-muted-foreground/60 transition-colors hover:text-gold"
              >
                <X className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>

          {geoNote && (
            <p className="mt-2 font-sans text-[0.7rem] text-muted-foreground">{geoNote}</p>
          )}
          {coords && (
            <p className="mt-1 font-sans text-[0.65rem] text-muted-foreground/70 tabular-nums">
              {coords.lat.toFixed(4)}, {coords.lon.toFixed(4)}
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="cf-message" className={labelClass}>
            Anything else? <span className={optionalClass}>optional</span>
          </label>
          <textarea
            id="cf-message"
            name="message"
            rows={3}
            value={form.message}
            onChange={e => set("message", e.target.value)}
            placeholder="The occasion, the look you're imagining, anything I should know."
            className={cn(fieldClass, "resize-y")}
          />
        </div>
      </div>

      {errors.form && (
        <p className="mt-6 flex items-center gap-1.5 font-sans text-[0.7rem] text-destructive">
          <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={1.5} />
          {errors.form}
        </p>
      )}

      <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="group inline-flex cursor-pointer items-center gap-3 bg-foreground px-8 py-4 font-sans text-[0.625rem] tracking-[0.24em] text-background uppercase transition-colors duration-300 hover:bg-gold disabled:cursor-wait disabled:opacity-70"
        >
          {status === "submitting" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
          ) : (
            <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
          )}
          {status === "submitting" ? "Sending…" : "Send enquiry"}
        </button>

        <p className="font-sans text-[0.65rem] leading-relaxed tracking-[0.08em] text-muted-foreground">
          Name and phone required. I reply personally, usually within a day.
        </p>
      </div>
    </form>
  );
}

/** Section wrapper so the form is wired into the page in one place. */
export function ContactSection() {
  return (
    <section id="contact" className="relative py-24 sm:py-32">
      <div className="mx-auto w-full max-w-3xl px-6 sm:px-8">
        <Reveal variant="fade">
          <span className="font-sans text-[0.625rem] tracking-[0.3em] text-gold uppercase">
            Get in touch
          </span>
        </Reveal>
        <Reveal variant="up" delay={0.08}>
          <h2 className="mt-4 font-display text-4xl leading-[1.1] text-foreground sm:text-5xl">
            Tell me about the occasion.
          </h2>
        </Reveal>
        <Reveal variant="fade" delay={0.16}>
          <p className="mt-5 max-w-xl font-sans text-sm leading-relaxed font-light text-muted-foreground">
            The more you share, the more useful my first reply can be. Or skip the form
            entirely and message me on WhatsApp — I read those too.
          </p>
        </Reveal>

        <Reveal variant="up" delay={0.24}>
          <div className="mt-12 border-t border-border pt-10">
            <ContactForm />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
